import { ColumnsQueryParams } from '@ui/table-modifier/ColumnsModal';
import { DataDictionaryTable } from './DataDictionaryTable';
import { getDataDictionarySelector } from './@arch/selectors';
import { getQueryString } from '@sonar-software/sonar-http';
import { keyPaginationDefaultQueryParams } from '@ui/KeyPagination';
import { PaginationDirection } from '@sonar-software/sonar-ts-types';
import { QuickAccess } from './search/QuickAccess';

import { capitalize, parseQueryString } from '@sonar-software/sonar-ts-lib';
import {
	coreActions,
	DataDictionaryBaseRequest,
	GetDataDictionaryByApplicationRequest,
	GetDataDictionaryByComplianceCategoryRequest,
	GetDataDictionaryByTagRequest,
	GetDataDictionaryByTypeRequest,
	GetDataDictionaryFieldsRequest
} from '@sonar-software/sonar-core';
import { DataDictionarySearchBar, DataDictionarySearchQueryParams } from './search';
import { DEFAULT_FILTER, FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { Else, If, Then } from 'react-if';
import { getAppliedFilters, getAppliedOrdering } from '@ui/table-modifier/@arch/lib';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAction, useAsyncState, useDataDictionaryReset, useOnMount, usePrevious, useQueryParams } from '@hooks';
import { useLocation, useNavigate } from 'react-router-dom';

export type DataDictionaryQueryParams = DataDictionaryBaseRequest &
	DataDictionarySearchQueryParams &
	ColumnsQueryParams & {
		lastViewId?: number;
		viewName?: string;
		viewSkip?: boolean;
	};

const defaultParams: DataDictionaryQueryParams = {
	...keyPaginationDefaultQueryParams,
	applicationId: undefined,
	columns: '',
	complianceCategory: undefined,
	displayName: undefined,
	filters: DEFAULT_FILTER,
	lastViewId: undefined,
	ordering: '',
	sonarObjectId: undefined,
	sonarObjectType: undefined,
	tagId: undefined,
	viewId: undefined,
	viewName: undefined
};

type ParamComparer = (newParams: Partial<DataDictionaryQueryParams>, prev: DataDictionaryQueryParams) => boolean;


function compareParams<T>(newParams: Partial<T>, prev: T) {
	return (param: keyof T) => newParams[ param ] !== undefined && newParams[ param ] !== prev[ param ];
}

function paramChanged<T>(newParams: Partial<T>, prev: T, param: keyof T) {
	return compareParams(newParams, prev)(param);
}

const hasEmptyParams: ParamComparer = (newParams, prev) =>
	Object.keys(newParams).length === 0 && Object.keys(prev).length !== 0;

const hasTargetChanged: ParamComparer = (newParams, prev) => {
	const changed = compareParams(newParams, prev);

	return changed('sonarObjectId') ||
		changed('sonarObjectType') ||
		changed('applicationId') ||
		changed('tagId') ||
		changed('complianceCategory');
};

const hasFilterChanged: ParamComparer = (newParams, prev) => {
	const changed = compareParams(newParams, prev);

	return changed('ordering') || changed('filters');
};

const hasPageSizeChanged: ParamComparer = (newParams, prev) => paramChanged(newParams, prev, 'count');

const hasPageChanged: ParamComparer = (newParams, prev) =>
	paramChanged(newParams, prev, 'key') || (!newParams.key && Boolean(prev.key));

const hasViewChanged: ParamComparer = (newParams, prev) =>
	(Boolean(newParams.viewId) && (!prev.viewId || newParams.viewId !== prev.viewId)) ||
	(Boolean(prev.lastViewId) && !newParams.viewId && !newParams.lastViewId);

const transformParams = (
	newParams: Partial<DataDictionaryQueryParams>,
	prev: DataDictionaryQueryParams,
	combined: DataDictionaryQueryParams
): Partial<DataDictionaryQueryParams> => {
	if (hasEmptyParams(newParams, prev)) {
		return defaultParams;
	} else if (hasTargetChanged(newParams, prev)) {
		const {
			applicationId,
			complianceCategory,
			displayName,
			sonarObjectId,
			sonarObjectType,
			tagId,
			viewId,
			viewName
		} = newParams;

		return {
			...defaultParams,
			...typeof complianceCategory === 'number' && { complianceCategory },
			displayName,
			...sonarObjectId && { sonarObjectId },
			...sonarObjectType && { sonarObjectType },
			...applicationId && { applicationId },
			...tagId && { tagId },
			...viewId && { viewId },
			...viewName && { viewName }
		};
	} else if (hasViewChanged(combined, prev)) {
		return combined;
	} else if (hasPageSizeChanged(newParams, prev)) {
		return {
			...combined,
			key: '',
			paginationDirection: PaginationDirection.Forward
		};
	} else if (hasFilterChanged(newParams, prev) && !hasViewChanged(newParams, prev)) {
		return {
			...combined,
			key: '',
			lastViewId: prev.viewId ?? prev.lastViewId,
			paginationDirection: PaginationDirection.Forward,
			viewId: undefined,
			viewName: prev.viewName
		};
	}

	return combined;
};

export const DataDictionary: React.FC = () => {
	const { data, isRequested, isReceived } = useAsyncState(getDataDictionarySelector);

	const getDataDictionary = useAction(coreActions.requestGetCoreDataDictionary);
	const updateView = useAction(coreActions.updateCoreDataDictionaryView);

	const { resetAll } = useDataDictionaryReset();

	const navigate = useNavigate();
	const location = useLocation();

	const parsedQuery = useMemo(
		() => parseQueryString(location.search) as DataDictionaryQueryParams,
		[ location.search ]
	);

	const updateUrl = useCallback(
		(queryObject: DataDictionaryQueryParams) => {
			navigate(
				`${FULL_ROUTES.DATA_DICTIONARY}?${getQueryString(queryObject as Record<string, unknown>)}`,
				{ replace: true }
			);
		},
		[ navigate ]
	);

	const callApi = useCallback(
		(queryObject: DataDictionaryQueryParams) => {
			const { columns, displayName, ...apiParams } = queryObject;

			switch (true) {
			case Boolean(apiParams.sonarObjectId):
				getDataDictionary(apiParams as GetDataDictionaryFieldsRequest);
				break;
			case Boolean(apiParams.sonarObjectType):
				getDataDictionary({
					...apiParams,
					sonarObjectType: capitalize(apiParams.sonarObjectType)
				} as GetDataDictionaryByTypeRequest);
				break;
			case Boolean(apiParams.tagId):
				getDataDictionary({
					...apiParams,
					tagId: apiParams.tagId
				} as GetDataDictionaryByTagRequest);
				break;
			case Boolean(apiParams.applicationId):
				getDataDictionary({
					...apiParams,
					applicationId: apiParams.applicationId
				} as GetDataDictionaryByApplicationRequest);
				break;
			case typeof apiParams.complianceCategory === 'number':
				getDataDictionary({
					...apiParams,
					complianceCategory: apiParams.complianceCategory
				} as GetDataDictionaryByComplianceCategoryRequest);
				break;
			default:
			}
		},
		[ getDataDictionary ]
	);

	const initialParams = useMemo(
		() => ({
			...defaultParams,
			filters: '',
			...parsedQuery
		}),
		[ parsedQuery ]
	);

	const appliedChanges = useRef<Partial<DataDictionaryQueryParams>>(initialParams);

	useOnMount(() => {
		if (
			initialParams.sonarObjectId ||
			initialParams.sonarObjectType ||
			initialParams.applicationId ||
			initialParams.tagId ||
			typeof initialParams.complianceCategory === 'number'
		) {
			callApi(initialParams);
		}
	});

	const onParamsUpdate = useCallback(
		(
			newParams: Partial<DataDictionaryQueryParams>,
			prev: DataDictionaryQueryParams,
			combined: DataDictionaryQueryParams
		) => {
			const { viewSkip, ...urlParams } = hasEmptyParams(newParams, prev) ?
				({} as DataDictionaryQueryParams) :
				combined;

			updateUrl(urlParams);

			if (hasEmptyParams(newParams, prev)) {
				resetAll();
			} else if (hasTargetChanged(newParams, prev)) {
				resetAll();
				callApi(urlParams);
			} else if (
				(hasViewChanged(combined, prev) && !viewSkip) ||
				hasPageSizeChanged(newParams, prev) ||
				hasPageChanged(newParams, prev) ||
				(hasFilterChanged(newParams, prev) && hasFilterChanged(newParams, appliedChanges.current))
			) {
				callApi(urlParams);
			}

			appliedChanges.current = urlParams;
		},
		[ appliedChanges, callApi, resetAll, updateUrl ]
	);

	const { queryParams, updateParams } = useQueryParams<DataDictionaryQueryParams>({
		defaultParams: initialParams,
		onUpdate: onParamsUpdate,
		transform: transformParams
	});

	const previousData = usePrevious(data, data);

	useEffect(() => {
		if (!data || data === previousData) {
			return;
		}

		const dataFilters = getAppliedFilters(data.appliedOrderingAndFilters);
		const dataOrdering = getAppliedOrdering(data.appliedOrderingAndFilters);
		const dataColumns = atob(data.appliedColumns ?? '');

		const { current } = appliedChanges;

		const filtersApplied = dataFilters === (current.filters ?? '');
		const orderingApplied = dataOrdering === (current.ordering ?? '');
		const columnsApplied = !current.viewId || dataColumns === (current.columns ?? '');

		const notApplied = !filtersApplied || !orderingApplied || !columnsApplied;

		if (notApplied) {
			const update: Partial<DataDictionaryQueryParams> = {
				lastViewId: current.viewId ?? current.lastViewId,
				viewId: undefined
			};

			if (!filtersApplied) {
				update.filters = dataFilters;
			}

			if (!orderingApplied) {
				update.ordering = dataOrdering;
			}

			if (!columnsApplied) {
				update.columns = dataColumns;
			}

			appliedChanges.current = {
				...current,
				...update
			};

			if (current.viewId) {
				updateView({
					columns: dataColumns,
					filter: dataFilters,
					id: current.viewId,
					ordering: dataOrdering
				});
			}

			updateParams(update);
		}
	}, [ data, previousData, updateParams, updateView ]);

	const previousUrl = usePrevious(location.search);

	useEffect(() => {
		const urlChanged = previousUrl && location.search !== previousUrl;

		if (!urlChanged) {
			return;
		}

		const latestParams = parseQueryString(location.search);
		const { current } = appliedChanges;

		if (hasTargetChanged(latestParams, current)) {
			updateParams({
				...defaultParams,
				...updateParams
			});

			return;
		}

		updateUrl(current);
	}, [ location.search, previousUrl, updateParams, updateUrl ]);

	return (
		<>
			<DataDictionarySearchBar
				queryParams={ queryParams }
				updateQuery={ updateParams } />
			<If condition={ isRequested || isReceived }>
				<Then>
					<DataDictionaryTable
						loading={ isRequested }
						queryParams={ queryParams }
						resource={ data }
						updateQuery={ updateParams } />
				</Then>
				<Else>
					<QuickAccess updateQuery={ updateParams } />
				</Else>
			</If>
		</>
	);
};
